import type { MachineId, TokenSpec } from '../../shared/contract.js';
import { ctxFor } from '../modifier.js';
import { hasTag, type TokenRuntime } from '../token.js';
import { v3, type Vec3 } from '../vec.js';
import { Cabinet, type CabinetHost, type FieldSolid, type TagTable } from './base.js';

/**
 * ROULETTE. You stake tokens *as* bets on the felt, and one of your tokens becomes the
 * ball. Both halves matter: a [Gummi] ball plays completely differently from a [Schwer]
 * one, so ball choice is a build slot (machines.md), and a bet left riding on the felt is
 * a real token body — which means [Zins] ripens it and [Spalt] splits it while it sits
 * there. "A bet left riding literally grows" is not a special case; it is the token being
 * a token.
 *
 * **DECISION — the orbit is integrated here; the rattle is real physics.** The ball's run
 * around the track is a 1-DOF constrained motion (angle, radius decay) integrated in this
 * file, exactly as the BANDIT's drums are, and for the same reason: a bead sliding round a
 * banked circular track gains nothing from a 3D solver and costs a body that never sleeps.
 * The moment it leaves the track it becomes an ordinary Rapier token dropped into the
 * pocket ring, where it genuinely rattles between the frets and settles. That is the half
 * players actually watch, and it is the half that has to be physical for [Gummi], [Schwer]
 * and [Magnet] to mean anything.
 *
 * The tag table is the declared one from machines.md and nothing more.
 */

export interface RouletteSetzen {
  /** which pocket this bet backs, 0..POCKETS-1 (0 is the green ZERO / TOPF mouth) */
  pocket: number;
  /** spin strength 0..1; only the first Setzen of a round launches the ball */
  power: number;
  spec: TokenSpec;
}

const POCKETS = 12;
const WHEEL = {
  /** the track the ball runs on before it drops */
  trackRadius: 5.2,
  /** the pocket ring it falls into */
  ringRadius: 3.4,
  rimY: 2.6,
  floorY: 0.6,
  fretHalf: 0.09,
  /** where staked bets sit: a felt ring outside the wheel */
  feltRadius: 8.2,
  feltY: 0.35,
} as const;

const TAU = Math.PI * 2;
const POCKET_ARC = TAU / POCKETS;

/** Straight-up on 1 of 12 pays 11:1; the green ZERO is the jackpot / TOPF mouth. */
const STRAIGHT_ODDS = 11;

interface Bet {
  token: number;
  pocket: number;
}

type Phase = 'idle' | 'orbit' | 'rattle' | 'settled';

export class Roulette extends Cabinet<RouletteSetzen> {
  readonly id: MachineId = 'roulette';
  readonly limitLabel = 'Tischmaximum — darüber hebt sich der Kessel aus und rollt den Gang hinunter.';
  readonly breakMode = 'wheel' as const;

  /** The declared odds mapping, straight out of machines.md. Data. The whole table. */
  readonly tagTable: TagTable = {
    Magnet: {
      effect: 'pullToStaked',
      note: 'Die Kugel wird zu Fächern gezogen, in denen Ihre Marken liegen.',
    },
    Fluch: {
      effect: 'invertPayTable',
      note: 'Die Auszahlungstafel dreht sich um: Außenseiter zahlen kurz, Einfache lang.',
    },
  };

  private solids: FieldSolid[] = [];
  private bets: Bet[] = [];
  private ball: number | null = null;
  private phase: Phase = 'idle';
  /** orbit state: angle on the track, angular velocity, current radius */
  private theta = 0;
  private omega = 0;
  private radius: number = WHEEL.trackRadius;
  /** the wheel head counter-rotates, as a real one does */
  private headTheta = 0;
  private headOmega = 0;
  private rounds = 0;
  private lastPocket = -1;
  private lastResult = 'noch kein Wurf';
  private tableMax = 260;
  private riding = false;

  build(): void {
    const phys = this.host.phys;

    // ---- the bowl. A ring of shell boxes: no ghost leaves the room through the rim.
    for (let i = 0; i < 16; i++) {
      const a = (i / 16) * TAU;
      const pos = this.w(v3(Math.cos(a) * (WHEEL.trackRadius + 0.9), WHEEL.rimY, Math.sin(a) * (WHEEL.trackRadius + 0.9)));
      phys.addStatic({ kind: 'box', hx: 0.3, hy: 1.6, hz: 1.3, pos }, 'shell', 0.2, 0.35);
    }
    // the wheel floor — solid to everything, a ghost must not fall through it
    phys.addStatic(
      { kind: 'cylinder', halfHeight: 0.3, radius: WHEEL.trackRadius + 1.0, pos: this.w(v3(0, WHEEL.floorY - 0.3, 0)) },
      'ground',
      0.55,
      0.12,
    );
    // the felt apron the bets sit on
    phys.addStatic(
      { kind: 'cylinder', halfHeight: 0.2, radius: WHEEL.feltRadius, pos: this.w(v3(0, WHEEL.feltY - 0.2, 0)) },
      'ground',
      0.92,
      0.02,
    );

    // ---- the frets: pocket separators, radiating. FIELD, so a [Geist] ball can slip one
    // and a [Kleber] ball can weld itself to one, which is a legal and very funny outcome.
    for (let i = 0; i < POCKETS; i++) {
      const a = i * POCKET_ARC;
      const pos = this.w(v3(Math.cos(a) * WHEEL.ringRadius, WHEEL.floorY + 0.35, Math.sin(a) * WHEEL.ringRadius));
      phys.addStatic({ kind: 'box', hx: WHEEL.fretHalf, hy: 0.35, hz: 0.6, pos }, 'field', 0.3, 0.42);
      this.solids.push({ kind: 'box', pos, h: v3(WHEEL.fretHalf, 0.35, 0.6) });
    }
    // the cone that guides the ball inward off the track
    const cone = this.w(v3(0, WHEEL.floorY + 0.8, 0));
    phys.addStatic({ kind: 'ball', radius: 1.5, pos: cone }, 'field', 0.25, 0.4);
    this.solids.push({ kind: 'ball', pos: cone, r: 1.5 });
  }

  fieldSolids(): readonly FieldSolid[] {
    return this.solids;
  }

  /** Verb 1: Setzen — place a bet on the felt; the first bet of a round spins the wheel. */
  setzen(p: RouletteSetzen): void {
    if (this.broken) return;
    if (this.phase === 'orbit' || this.phase === 'rattle') return; // rien ne va plus

    const pocket = ((Math.round(p.pocket) % POCKETS) + POCKETS) % POCKETS;
    // A bet is a real token lying on the felt at its pocket's angle. It stays there,
    // ageing, until it is collected — which is the whole [Zins] interaction.
    const a = pocket * POCKET_ARC;
    const pos = this.w(v3(Math.cos(a) * WHEEL.feltRadius * 0.82, WHEEL.feltY + 0.6, Math.sin(a) * WHEEL.feltRadius * 0.82));
    const tok = this.host.spawnToken(p.spec, this.id, pos, v3(0, -0.4, 0));
    this.bets.push({ token: tok.id, pocket });
    this.host.emit({ t: 'drop', token: tok.id, machine: this.id });
    this.host.pipe.fire(
      'onDrop',
      { ...ctxFor(this.id, this.host.tick, tok), api: this.host.api },
      { token: tok.id },
    );
    this.spin(p.power, p.spec);
  }

  /** One of your tokens becomes the ball. It is drawn from Der Beutel like any other. */
  private spin(power: number, betSpec: TokenSpec): void {
    const pw = clamp(power, 0, 1);
    const ballSpec = this.host.drawFromBeutel('roulette:ball');
    // A ball with no enchantments at all is a dull build slot; if the bag hands us a bare
    // coin we still use it — the point is that the bag decides, not the machine.
    void betSpec;
    const start = this.host.rng.float('roulette:launch') * TAU;
    this.theta = start;
    this.radius = WHEEL.trackRadius;
    // heavier balls carry further, bouncier ones scrub less — mass and restitution are
    // pipeline answers, so [Schwer] and [Gummi] change the run length for free
    const ctx = ctxFor(this.id, this.host.tick, null);
    const mass = this.host.pipe.query('mass', ctx, 1);
    this.omega = (7.5 + 5.5 * pw) * (1 + (mass - 1) * 0.12);
    this.headOmega = -1.4 - 0.6 * pw;

    const bpos = this.w(v3(Math.cos(start) * WHEEL.trackRadius, WHEEL.rimY - 0.2, Math.sin(start) * WHEEL.trackRadius));
    const tok = this.host.spawnToken(ballSpec, this.id, bpos, v3(0, 0, 0), false);
    this.ball = tok.id;
    this.phase = 'orbit';
    this.rounds++;
    this.riding = false;
  }

  /** Verb 2 reaction: one table bump while the ball runs. Scandalous; VERDACHT notices. */
  protected override onShove(strength: number): void {
    if (this.phase === 'orbit') {
      // a bump scrubs the ball's run and shifts where it drops — the classic sin
      this.omega *= 1 - 0.10 * strength;
      this.theta += (this.host.rng.float('roulette:bump') - 0.5) * 0.5 * strength;
      this.host.economy.addVerdacht(2, 'Tischstoß bei laufender Kugel', (e) => this.host.emit(e));
    }
  }

  protected escalate(level: number): void {
    // Nachlegen: let the winnings ride. Bets are not collected between rounds, and the
    // table maximum climbs with them — which is what eventually unseats the wheel.
    this.riding = true;
    this.tableMax = 260 + 180 * (level - 1);
  }

  tickMachine(dt: number): void {
    this.tickShared(dt);
    if (this.broken) return;

    this.headTheta = (this.headTheta + this.headOmega * dt) % TAU;

    if (this.phase === 'orbit') this.orbit(dt);
    else if (this.phase === 'rattle') this.rattle();

    // bets that have been shoved off the felt are gone (or roll into the room)
    for (const b of [...this.bets]) {
      const t = this.host.api.token(b.token);
      if (!t) {
        this.bets = this.bets.filter((x) => x.token !== b.token);
        continue;
      }
      const p = this.host.api.position(b.token);
      if (!p) continue;
      const local = { x: p.x - this.origin.x, y: p.y - this.origin.y, z: p.z - this.origin.z };
      if (local.y < -1.5 || Math.hypot(local.x, local.z) > WHEEL.feltRadius + 4) {
        this.bets = this.bets.filter((x) => x.token !== b.token);
        this.host.escaped(t, this, this.escapeMode(t), p);
      }
    }

    this.checkLimit();
  }

  /** The 1-DOF run around the track: spin down, spiral in, then drop into the ring. */
  private orbit(dt: number): void {
    const ball = this.ball !== null ? this.host.api.token(this.ball) : null;
    if (!ball) {
      this.phase = 'idle';
      return;
    }

    // friction on the track. A [Gummi] ball keeps its speed longer; restitution is a
    // pipeline answer, so the tag never appears in this file.
    const rest = this.host.pipe.query('restitution', ctxFor(this.id, this.host.tick, ball), 0.32);
    const drag = 0.72 - 0.22 * clamp(rest, 0, 1);
    this.omega = Math.max(0, this.omega - drag * dt);

    // [Magnet] — the declared mapping: the ball is drawn toward pockets where the player
    // has staked. Physical in spirit and honest in effect: it biases the run, it does not
    // teleport the result, and a magnet with no bets on the felt does nothing at all.
    if (this.tagTable.Magnet?.effect === 'pullToStaked' && hasTag(ball, 'Magnet') && this.bets.length > 0) {
      const target = this.nearestStakedAngle(this.theta);
      if (target !== null) {
        let d = wrapPi(target - this.theta);
        // strength falls off with speed: you cannot steer a ball that is still flying
        const grip = clamp(1 - this.omega / 9, 0, 1) * 0.9 * ball.potency;
        this.theta += d * grip * dt;
        d = 0;
      }
    }

    this.theta = (this.theta + this.omega * dt) % TAU;
    // spiral inward as it slows
    const t01 = clamp(1 - this.omega / 6.5, 0, 1);
    this.radius = WHEEL.trackRadius - (WHEEL.trackRadius - WHEEL.ringRadius - 0.5) * t01;

    if (this.omega <= 1.6) {
      // it leaves the track. From here it is an ordinary Rapier body among the frets.
      const a = this.theta;
      const pos = this.w(v3(Math.cos(a) * this.radius, WHEEL.floorY + 1.4, Math.sin(a) * this.radius));
      const tangential = this.omega * this.radius * 0.35;
      const vel = v3(-Math.sin(a) * tangential, -1.2, Math.cos(a) * tangential);
      this.materialise(ball, pos, vel);
      this.phase = 'rattle';
    }
  }

  /** Which staked pocket is closest ahead of the ball? Deterministic tie-break by pocket. */
  private nearestStakedAngle(from: number): number | null {
    let best: { angle: number; d: number; pocket: number } | null = null;
    for (const b of this.bets) {
      const t = this.host.api.token(b.token);
      if (!t) continue;
      const a = b.pocket * POCKET_ARC;
      const d = Math.abs(wrapPi(a - from));
      if (!best || d < best.d || (d === best.d && b.pocket < best.pocket)) {
        best = { angle: a, d, pocket: b.pocket };
      }
    }
    return best ? best.angle : null;
  }

  /** Give the abstract ball a real body so it can rattle between the frets. */
  private materialise(ball: TokenRuntime, pos: Vec3, vel: Vec3): void {
    const spec: TokenSpec = {
      id: ball.id,
      material: ball.material,
      faceValue: ball.faceValue,
      radius: ball.radius,
      tags: [...ball.tags],
    };
    this.host.removeToken(ball.id, 'transfer');
    const real = this.host.spawnToken(spec, this.id, pos, vel);
    this.ball = real.id;
  }

  /** Watch the ball until it stops moving, then read the pocket it is sitting in. */
  private rattle(): void {
    const ball = this.ball !== null ? this.host.api.token(this.ball) : null;
    if (!ball || ball.body < 0) {
      this.phase = 'idle';
      this.ball = null;
      return;
    }
    const p = this.host.api.position(ball.id);
    if (!p) return;
    const local = { x: p.x - this.origin.x, y: p.y - this.origin.y, z: p.z - this.origin.z };

    if (local.y < -1.5 || Math.hypot(local.x, local.z) > WHEEL.trackRadius + 3.5) {
      // the ball left the wheel entirely — a dud, and with Der Deckel it leaves the cabinet
      this.registerDud(ball);
      this.lastResult = 'Kugel aus dem Kessel';
      this.phase = 'settled';
      this.host.escaped(ball, this, this.escapeMode(ball), p);
      this.ball = null;
      this.settleRound(-1);
      return;
    }

    const v = this.host.phys.linvel(ball.body);
    const speed = Math.abs(v.x) + Math.abs(v.y) + Math.abs(v.z);
    if (speed > 0.35) return;
    if (ball.idleSeconds < 0.35 && !ball.stuck) return;

    // read the pocket off the ball's angle, corrected for the counter-rotating head
    const a = Math.atan2(local.z, local.x) - this.headTheta;
    const pocket = ((Math.round(a / POCKET_ARC) % POCKETS) + POCKETS) % POCKETS;
    this.phase = 'settled';
    this.lastPocket = pocket;
    this.host.emit({ t: 'wheelSettle', pocket, token: ball.id });
    this.settleRound(pocket);
  }

  /** Pay the bets that backed the winning pocket; the rest are duds that feed the pot. */
  private settleRound(pocket: number): void {
    const winners: Bet[] = [];
    const losers: Bet[] = [];
    for (const b of this.bets) {
      if (!this.host.api.token(b.token)) continue;
      (b.pocket === pocket ? winners : losers).push(b);
    }

    for (const b of losers) {
      const t = this.host.api.token(b.token);
      if (!t) continue;
      // a bet one pocket away is the near-miss this table banks rather than exploits
      const gap = pocket < 0 ? 99 : Math.min(
        Math.abs(b.pocket - pocket),
        POCKETS - Math.abs(b.pocket - pocket),
      );
      if (gap === 1) this.feedTopf(Math.max(1, Math.round(t.faceValue * 0.6)));
      this.registerDud(t);
    }

    for (const b of winners) {
      const t = this.host.api.token(b.token);
      if (!t) continue;
      const p = this.host.api.position(b.token) ?? this.w(v3(0, 1, 0));
      const zero = b.pocket === 0;
      const base = t.faceValue * this.payMultiplier(t, zero);
      this.registerScore(t, zero ? 'ZERO' : `f${b.pocket}`, base, p, zero);
    }

    this.lastResult = pocket < 0
      ? 'kein Ergebnis'
      : `${pocket === 0 ? 'ZERO' : pocket} · ${winners.length} Treffer`;

    // bets are collected unless the player let them ride (Nachlegen)
    if (!this.riding) {
      for (const b of [...winners, ...losers]) {
        const t = this.host.api.token(b.token);
        if (t) this.host.removeToken(b.token, winners.includes(b) ? 'scored' : 'lost');
      }
      this.bets = [];
    } else {
      // riding bets survive and keep ageing on the felt — [Zins] compounds them
      this.bets = this.bets.filter((b) => this.host.api.token(b.token) !== null);
    }

    if (this.ball !== null) {
      const ball = this.host.api.token(this.ball);
      if (ball) this.host.removeToken(this.ball, 'lost');
      this.ball = null;
    }
    this.phase = 'idle';
  }

  /**
   * [Fluch] — the declared mapping: the pay table inverts. A longshot (the straight-up
   * ZERO) pays short and the ordinary pockets pay long. Cursed material still multiplies
   * by zero at the material stage, so a cursed *bet* cannot cash in; this inversion is
   * what makes a cursed *ball* worth bringing.
   */
  private payMultiplier(t: TokenRuntime, zero: boolean): number {
    const cursed = this.tagTable.Fluch?.effect === 'invertPayTable' && hasTag(t, 'Fluch');
    if (!cursed) return zero ? STRAIGHT_ODDS * 2 : STRAIGHT_ODDS;
    return zero ? 2 : STRAIGHT_ODDS * 2;
  }

  structuralLoad(): number {
    let staked = 0;
    for (const b of this.bets) {
      const t = this.host.api.token(b.token);
      if (t) staked += t.faceValue;
    }
    return staked / this.tableMax;
  }

  burstValue(): number {
    let v = this.topf;
    for (const b of this.bets) {
      const t = this.host.api.token(b.token);
      if (t) v += t.faceValue * STRAIGHT_ODDS;
    }
    return v + 120 * this.stakeLevel;
  }

  protected override collectRemaining(): { muenzen: number; tickets: number } {
    // walking away with bets on the felt leaves them to the House
    let muenzen = 0;
    for (const b of this.bets) {
      const t = this.host.api.token(b.token);
      if (!t) continue;
      muenzen += Math.round(t.faceValue);
      this.host.removeToken(b.token, 'scored');
    }
    this.bets = [];
    this.riding = false;
    return { muenzen, tickets: 0 };
  }

  extra(): unknown {
    return {
      pockets: POCKETS,
      phase: this.phase,
      theta: this.theta,
      radius: this.radius,
      headTheta: this.headTheta,
      ball: this.ball,
      bets: this.bets.map((b) => ({ token: b.token, pocket: b.pocket })),
      lastPocket: this.lastPocket,
      lastResult: this.lastResult,
      tableMax: this.tableMax,
      riding: this.riding,
      rounds: this.rounds,
      wheel: {
        trackRadius: WHEEL.trackRadius,
        ringRadius: WHEEL.ringRadius,
        feltRadius: WHEEL.feltRadius,
        rimY: WHEEL.rimY,
        floorY: WHEEL.floorY,
      },
    };
  }
}

const clamp = (x: number, lo: number, hi: number): number => (x < lo ? lo : x > hi ? hi : x);
/** shortest signed angular distance, (-π, π] */
function wrapPi(a: number): number {
  let x = a % TAU;
  if (x > Math.PI) x -= TAU;
  if (x <= -Math.PI) x += TAU;
  return x;
}

export { POCKETS as ROULETTE_POCKETS, type CabinetHost };
